import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
  memo,
} from "react";
import { TooltipProps } from "./types";
import { Callout } from "../Callout/Callout";
import cx from "components/src/utils/cx";
import { useIdRef } from "shared/src/hooks/useIdRef";
import { addProps } from "shared/src/utils/addProps";
import { calculatePositions } from "./js/calculatePositions";
import { classifyInViewport } from "./js/classifyInViewport";
import { calculatePlacement } from "./js/calculatePlacement";
import debounce from "lodash-es/debounce";

function verifyState(internal, external, isFirstRender) {
  if (isFirstRender) return internal;
  if (!internal && external) {
    return true;
  } else if (internal && !external) {
    return false;
  }
  if (internal === false && external === false) return true;
  return internal && external;
}

const _Tooltip = ({
  id,
  title,
  description,
  image,
  imageWidth,
  imageHeight,
  badgeText,
  icon,
  primaryCtaText,
  secondaryCtaText,
  isOpen: controlledIsOpen,
  anchorRef: controlledAnchorRef,
  children,
  trigger = "click",
  closeOnOutsideClick = false,
  disabled = false,
  pointerPlacement = "auto",
  offset = 8,
  width = 360,
  maxWidth = "90vw",
  minWidth = 100,
  mode = "light",
  className,
  zIndex = 9999,
  fixed,
  preventTriggerInteraction,
  "aria-label": ariaLabel,
  onTooltipInteraction,
  onClose,
  onPrimaryCtaClick,
  onSecondaryCtaClick,
  ...rest
}: TooltipProps) => {
  const [isOpen, setIsOpen] = useState(controlledIsOpen ?? false);
  const [triggerPos, setTriggerPos] = useState<{
    placement: "top" | "left" | "right" | "bottom";
    xOffset: number;
    yOffset: number;
  }>({ placement: "top", xOffset: 0, yOffset: 0 });
  const [tooltipPos, setTooltipPos] = useState<{ transform: string } | {}>({});
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const isFirstRenderRef = useRef(true);
  const _id = useIdRef({ str: "cui__tooltip-", id }).current;
  const calloutId = `${_id}-body`;
  const tooltipTriggerId = `${_id}-trigger`;

  const handleTriggerClick = useCallback(() => {
    setIsOpen((v) => !v);
  }, [controlledIsOpen]);

  const handleCalloutOnClose = useCallback(
    (e) => {
      if (isOpen) {
        setIsOpen(false);
        if (typeof onClose === "function") {
          onClose({ e, id: _id });
        }
      }
    },
    [onClose, isOpen]
  );

  const { primaryCta, secondaryCta } = useMemo(() => {
    return {
      primaryCta: primaryCtaText
        ? {
            text: primaryCtaText,
          }
        : undefined,
      secondaryCta: secondaryCtaText
        ? {
            text: secondaryCtaText,
          }
        : undefined,
    };
  }, [primaryCtaText, secondaryCtaText]);

  const ChildrenWithTrigger = useMemo(() => {
    if (!children) return <div />;

    const _handleClick = () => {
      children?.props.onClick && children?.props.onClick();
      !preventTriggerInteraction && handleTriggerClick();
    };

    if (typeof children !== "object" && typeof children === "string")
      return <div onClick={_handleClick}>{children}</div>;
    return addProps(children, {
      onClick: _handleClick,
      "data-id": tooltipTriggerId,
    });
  }, [children]);

  /**
   * Hooks
   */

  // onTooltipInteraction
  useEffect(() => {
    if (typeof controlledIsOpen === "boolean") {
      setIsOpen((v) => verifyState(v, controlledIsOpen, isFirstRenderRef.current));
    }
  }, [controlledIsOpen]);

  useEffect(() => {
    if (isOpen && anchorRef.current) {
      const tooltip = anchorRef.current.children[1] as HTMLElement;
      tooltip.focus();
    }
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      return;
    }
    typeof onTooltipInteraction === "function" &&
        onTooltipInteraction({ id: _id, isOpen });
  }, [isOpen, onTooltipInteraction]);

  // Close on outside click
  useEffect(() => {
    if (!closeOnOutsideClick || !anchorRef.current) return;
    const tooltip = anchorRef.current.children[1];
    const trigger = anchorRef.current.children[0];
    function handleBodyClick(e) {
      const isOutsideClick =
        !tooltip.contains(e.target) && !trigger.contains(e.target);
      // console.log({ eTarget: e.target, tooltip, trigger, isOutsideClick, whatsThis: (e.target.offsetParent.contains(tooltip) && e.target !== tooltip)  });
      if (isOutsideClick) setIsOpen(false);
    }
    document?.body.addEventListener("click", handleBodyClick);
    if (!isOpen) document?.body.removeEventListener("click", handleBodyClick);
    return () => {
      document?.body.removeEventListener("click", handleBodyClick);
    };
  }, [isOpen, closeOnOutsideClick, controlledIsOpen]);

  useEffect(() => {
    if (!anchorRef.current) return;
    if (isFirstRenderRef.current) return;
    const trigger = anchorRef.current.children[0];
    const tooltip = anchorRef.current.children[1];

    const calculateTooltip = debounce(
      (e) => {
        const location = classifyInViewport(trigger);
        const _pointerPlacement =
          pointerPlacement === "auto"
            ? calculatePlacement(location)
            : pointerPlacement;
        // console.log({_pointerPlacement, e})
        calculatePositions({
          setTriggerPos,
          setTooltipPos,
          trigger,
          tooltip,
          pointerPlacement: _pointerPlacement,
          scrollEvent: e?.target?.documentElement,
          fixed,
        });
      },
      fixed ? 20 : 100
    );

    calculateTooltip({
      target: { documentElement: { scrollTop: window?.scrollY } },
    });

    if (isOpen) {
      document?.addEventListener("scroll", calculateTooltip, { passive: true });
    }
    return () => {
      document?.removeEventListener("scroll", calculateTooltip);
    };
  }, [isOpen, pointerPlacement, fixed]);

  return (
    <div
      id={_id}
      className="cui__tooltip-container cui-relative cui-w-auto cui-h-auto cui-inline-block"
      ref={anchorRef}
    >
      {ChildrenWithTrigger}
      <div
        id={`${_id}-tooltip-wrapper`}
        className={cx(
          {
            "cui-invisible cui-opacity-0": !isOpen, // && !controlledIsOpen,
            "cui-visible cui-opacity-100": isOpen, //|| controlledIsOpen,
            "cui-absolute": !!!fixed,
            "cui-fixed": !!fixed,
          },
          `cui__tooltip-wrapper cui-transition-opacity cui-duration-200`
        )}
        style={{ ...tooltipPos, zIndex }}
      >
        <Callout
          id={calloutId}
          title={title}
          description={description}
          image={image}
          icon={icon}
          badge={badgeText}
          status="tooltip"
          visible={true}
          minWidth={minWidth}
          maxWidth={maxWidth}
          className="cui-bg-status-informational cui-bg-status-informational cui-shadow-lg"
          mode={mode}
          role="tooltip"
          aria-label={ariaLabel ?? title}
          aria-describedby={description ? "tooltip-description" : undefined}
          withArrow
          arrowPosition={triggerPos}
          showPrimaryCta={!!primaryCta}
          showSecondaryCta={!!secondaryCta}
          primaryCta={primaryCta}
          secondaryCta={secondaryCta}
          ref={tooltipRef}
          onClose={handleCalloutOnClose}
          onPrimaryCtaClick={onPrimaryCtaClick}
          onSecondaryCtaClick={onSecondaryCtaClick}
          {...rest}
        />
      </div>
    </div>
  );
};

export const Tooltip = memo(_Tooltip);
